/**
 * Variant Merchandised Grid - AJAX load-more.
 *
 * Used by collections rendered with sections/template-collection.liquid
 * when collection.metafields.custom.merchandised_variants is populated.
 *
 * In merchandised mode the section suppresses #storefront-filters-parent,
 * which disables the existing storefrontFilters infinite-scroll wiring.
 * This script provides an equivalent: it intercepts clicks on
 * [data-merch-loadmore], fetches the next page through Shopify's
 * section-rendering API, appends the new product cards into
 * [data-products-grid], and replaces the load-more button with the next
 * page's button (or removes it on the final page). An IntersectionObserver
 * auto-triggers the click as the button approaches the viewport, matching
 * the body.infinite-scroll-active behaviour of the native filter system.
 */
(() => {
  const SECTION_ID_ATTR = 'data-section-id';
  const PARENT_SELECTOR = '[data-merch-pagination-parent]';
  const LOADMORE_SELECTOR = '[data-merch-loadmore]';
  const GRID_SELECTOR = '[data-products-grid]';

  const parent = document.querySelector(PARENT_SELECTOR);
  if (!parent) return;

  const sectionId = parent.getAttribute(SECTION_ID_ATTR);
  const grid = document.querySelector(GRID_SELECTOR);
  if (!sectionId || !grid) return;

  let isLoading = false;
  let observer = null;

  /**
   * Append `?sections=<id>` (or `&sections=...`) to a URL.
   */
  const withSection = (rawHref) => {
    const url = new URL(rawHref, window.location.origin);
    url.searchParams.set('sections', sectionId);
    return url.toString();
  };

  /**
   * Hydrate components and third-party badges that were rendered server-side
   * for the newly appended cards. Mirrors the post-render hooks used by
   * assets/storefront-filters.js renderProductGrid().
   */
  const onCardsAppended = () => {
    if (typeof lazyImageObserver !== 'undefined' && lazyImageObserver?.observe) {
      lazyImageObserver.observe();
    }
    if (typeof StampedFn !== 'undefined' && StampedFn?.loadBadges) {
      StampedFn.loadBadges();
    }
  };

  /**
   * Replace the load-more button (or remove it on the last page) and
   * re-attach the click + observer to the new node.
   */
  const swapLoadMore = (nextDoc) => {
    const currentButton = parent.querySelector(LOADMORE_SELECTOR);
    const nextButton = nextDoc.querySelector(LOADMORE_SELECTOR);

    if (currentButton) {
      if (nextButton) {
        currentButton.parentNode.replaceChild(nextButton, currentButton);
        attachLoadMoreHandlers(nextButton);
      } else {
        currentButton.remove();
        if (observer) {
          observer.disconnect();
          observer = null;
        }
        document.body.classList.remove('infinite-scroll-active');
      }
    }
  };

  const handleLoadMoreClick = async (event) => {
    event.preventDefault();
    if (isLoading) return;
    const button = event.currentTarget;
    if (!button || !button.href) return;

    isLoading = true;
    button.classList.add('loading');
    button.style.pointerEvents = 'none';
    const originalText = button.textContent;
    const loadingLabel = button.getAttribute('data-loading-label') || originalText;
    button.textContent = loadingLabel;

    try {
      // No Accept header: Shopify routes by ?sections=. With
      // Accept: application/json the dev proxy returns the
      // collection JSON API instead, which lacks our section key.
      const response = await fetch(withSection(button.href));
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const sectionHTML = data[sectionId];
      if (!sectionHTML) throw new Error(`Section "${sectionId}" missing in response`);

      const parsed = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const nextGrid = parsed.querySelector(GRID_SELECTOR);
      if (nextGrid) {
        grid.insertAdjacentHTML('beforeend', nextGrid.innerHTML);
      }
      swapLoadMore(parsed);
      onCardsAppended();
    } catch (err) {
      console.error('[merch-loadmore] failed to load next page:', err);
      button.textContent = originalText;
      button.classList.remove('loading');
      button.style.pointerEvents = '';
    } finally {
      isLoading = false;
    }
  };

  /**
   * Wire click + IntersectionObserver onto the active load-more button so
   * scrolling to within ~200px auto-fires the next page fetch.
   */
  const attachLoadMoreHandlers = (button) => {
    button.addEventListener('click', handleLoadMoreClick);

    if (!('IntersectionObserver' in window)) return;

    document.body.classList.add('infinite-scroll-active');

    if (!observer) {
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting && !isLoading) {
              entry.target.click();
            }
          }
        },
        { root: null, rootMargin: '200px', threshold: 0.1 }
      );
    }
    observer.observe(button);
  };

  const initialButton = parent.querySelector(LOADMORE_SELECTOR);
  if (initialButton) attachLoadMoreHandlers(initialButton);
})();
